/**
 * Role guard. Wraps `RequireAuth` and additionally checks the caller has
 * the required role via the `/api/v1/me` response.
 *
 * UI-only — the backend re-checks every request (CLAUDE.md §5.3). This
 * guard exists so admin pages don't briefly flash before redirecting.
 *
 * On denial, renders `<ForbiddenPage>` rather than silently bouncing the
 * caller back to /me — operators reported the silent redirect read as a
 * broken nav link, not an authorisation outcome.
 */

import type { PropsWithChildren } from "react";
import { useMe } from "@/features/users/useMe";
import { useEffectiveRoles } from "./RolePreviewContext";
import { ForbiddenPage } from "./ForbiddenPage";
import { RequireAuth } from "./RequireAuth";

interface Props {
  /** Single role string, or an array — caller passes when an action is
   *  shared across roles (e.g. `audit.read` is granted to both
   *  `developer` and `administrator`). Role inheritance lives in the
   *  backend policy layer; the local mirror is flat, so the UI lists
   *  every acceptable role explicitly. */
  role: string | readonly string[];
}

export function RequireRole({ role, children }: PropsWithChildren<Props>) {
  return (
    <RequireAuth>
      <RequireRoleInner role={role}>{children}</RequireRoleInner>
    </RequireAuth>
  );
}

function RequireRoleInner({ role, children }: PropsWithChildren<Props>) {
  const { data, loading } = useMe();
  // Honour an admin's "view as role" preview (no-op for everyone else). The
  // backend still enforces the real role on every request — this only gates
  // what the UI shows.
  const effectiveRoles = useEffectiveRoles(data?.roles ?? []);
  if (loading) return <p>Checking access…</p>;
  const accepted = typeof role === "string" ? [role] : role;
  const ok = accepted.some((r) => effectiveRoles.includes(r));
  if (!ok) {
    return <ForbiddenPage requiredRole={accepted.join(" / ")} callerRoles={effectiveRoles} />;
  }
  return <>{children}</>;
}
